import { primitives } from './primitives';

/**
 * Semantic tokens — the single source of truth for what a color *means*
 * (role-based aliases over the raw `primitives` ramp). The MUI theme derives
 * every semantic palette slot from here; components should consume the theme
 * (`theme.palette` / `sx`), not this module directly. See styles/README.md and
 * docs/DESIGN_SYSTEM.md (Token pipeline).
 */
export const tokens = {
  // Pure surfaces — re-exported so nothing downstream writes a bare '#fff'.
  common: {
    white: primitives.common.white,
    black: primitives.common.black,
  },

  // Brand / Primary — Figma Deep Blue = brand/125, Blue Dark = brand/128
  primary: {
    lighter: primitives.brand[50],
    light: primitives.brand[75],
    main: primitives.brand[100],
    dark: primitives.brand[120],
    darker: primitives.brand[128],
    contrastText: primitives.common.white,
  },

  // Informational / sky accent
  info: {
    lighter: primitives.brand[50],
    light: primitives.info.light,
    main: primitives.brand[100],
    dark: primitives.info.dark,
    darker: primitives.brand[128],
    contrastText: primitives.common.white,
  },

  // Functional Colors
  success: {
    light: primitives.green[25],
    main: primitives.green[100],
    dark: primitives.green[125],
    contrastText: primitives.common.white,
  },
  error: {
    light: primitives.red[25],
    main: primitives.red[100],
    dark: primitives.red[125],
    contrastText: primitives.common.white,
  },
  warning: {
    light: primitives.yellow[25],
    main: primitives.yellow[100],
    dark: primitives.yellow[150],
    contrastText: primitives.neutrals.opacity[0],
  },

  // Text Colors — Figma `colors/grey/*`. This corrects the historical secondary/tertiary
  // transposition (secondary is the darker #414E55, tertiary the lighter #6B757B) and points
  // disabled at the dedicated Figma token (#B0B5B8).
  text: {
    primary: primitives.grey.primary,
    secondary: primitives.grey.secondary,
    tertiary: primitives.grey.tertiary,
    disabled: primitives.grey.disabled,
    inverse: primitives.common.white,
    /** Figma `colors/grey/placeholder` — an empty input's ink. */
    placeholder: primitives.grey.placeholder,
    /** #747D85 — Figma's label ink (field labels, weekday letters); no palette variable. */
    label: '#747D85',
  },

  // Backgrounds
  background: {
    /**
     * The app canvas — #F8F8FA, so it reads distinct from `paper` (white) by fill.
     *
     * History (resolved): the canvas was briefly whitened (2026-07-16) to lift `text.secondary`
     * over the 4.5 AA threshold. That masked a transposition — Figma's `text/secondary` is #414E55,
     * not #6B757B (which is `text/tertiary`). The Figma palette migration corrects `text` above to
     * the `grey` group, so secondary body copy is now #414E55 (≈8:1 on this canvas) and passes AA
     * without whitening the canvas.
     */
    default: primitives.neutrals.solid[120],
    paper: primitives.common.white,
    subtle: primitives.neutrals.solid[100],
    neutral: primitives.neutrals.solid[90],
  },

  // Borders / Dividers
  border: {
    light: primitives.neutrals.solid[100],
    main: primitives.neutrals.solid[90],
    dark: primitives.neutrals.solid[80],
  },

  // Neutrals Solid
  grey: {
    120: primitives.neutrals.solid[120],
    100: primitives.neutrals.solid[100],
    90: primitives.neutrals.solid[90],
    80: primitives.neutrals.solid[80],
    70: primitives.neutrals.solid[70],
    60: primitives.neutrals.solid[60],
    50: primitives.neutrals.solid[50],
    40: primitives.neutrals.solid[40],
    30: primitives.neutrals.solid[30],
    20: primitives.neutrals.solid[20],
    10: primitives.neutrals.solid[10],
    0: primitives.neutrals.solid[0],
  },

  brand: {
    10: primitives.brand[10],
    25: primitives.brand[25],
    50: primitives.brand[50],
    75: primitives.brand[75],
    80: primitives.brand[80],
    100: primitives.brand[100],
    120: primitives.brand[120],
    125: primitives.brand[125],
    128: primitives.brand[128],
    150: primitives.brand[150],
    175: primitives.brand[175],
  },

  green: {
    10: primitives.green[10],
    25: primitives.green[25],
    50: primitives.green[50],
    75: primitives.green[75],
    100: primitives.green[100],
    125: primitives.green[125],
  },

  orange: {
    25: primitives.orange[25],
    50: primitives.orange[50],
    100: primitives.orange[100],
    150: primitives.orange[150],
  },

  darkBlue: {
    25: primitives.darkBlue[25],
    100: primitives.darkBlue[100],
    150: primitives.darkBlue[150],
  },

  purple: {
    25: primitives.purple[25],
    75: primitives.purple[75],
    100: primitives.purple[100],
    150: primitives.purple[150],
  },

  /**
   * Data-analysis Views workspace (Figma "Data analysis" boards). Role aliases
   * over the shared ramps — every value derives from `primitives` (the teal
   * accent system IS the brand ramp). Consumed exclusively through the theme
   * (`palette.custom.dataAnalysis`); importing this module inside the slice is
   * a lint error.
   */
  dataAnalysis: {
    surface: primitives.common.white,
    surfaceSubtle: primitives.neutrals.solid[120],
    surfaceMuted: primitives.neutrals.solid[100],
    border: primitives.neutrals.solid[90],
    borderStrong: primitives.neutrals.solid[80],
    borderStronger: primitives.neutrals.solid[70], // #B2C5C8 — active control border (e.g. "+" menu-open)

    textStrong: primitives.neutrals.solid[0],
    textHeading: primitives.neutrals.textBody[0],
    textBody: primitives.neutrals.textBody[10],
    textMuted: primitives.neutrals.textBody[20],
    textSoft: primitives.neutrals.solid[50],
    /** #A0B7BA — Figma `neutrals/icon/default`: config drag grips + muted row glyphs. */
    iconMuted: primitives.neutrals.solid[80],
    /** #1A5159 — `neutral-solid/20` as an ICON ink: the ⇄ on every correction-journal
     *  row (Withdrawn included — only its stroke greys) and the plus of "Add
     *  correction" (Step 11 `39374:110486` / `110524` / `110532`). The same primitive
     *  is `avatarBg`; named again because a glyph ink and a fill are free to diverge. */
    iconStrong: primitives.neutrals.solid[20],
    /** #4D787E — teal-tinted meta ink (panel backlinks/edited line, chip counters). */
    textMutedTeal: primitives.neutrals.solid[40],
    /** #899499 — Figma `colors/grey/placeholder`: the placeholder ink of the Finding
     *  host's date, account and description inputs (Step 11 `39374:110483`). The
     *  ramp step exists in `primitives.grey` but no `palette.text` slot carries it. */
    textPlaceholder: primitives.grey.placeholder,
    /**
     * #747D85 — SAMPLED from the Step 11 frame (`fill_5afef0b4`: the Finding host's
     * field labels, its meta row and the Withdrawn chip's ink; also the card header's
     * "Import Wizard"), not derived from the ramp: `grey.tertiary` is #6B757B and no
     * other step lands on it, so — like `unmapped.bg` — the hex is stated here once
     * rather than nearest-matched in a component.
     */
    textLabel: '#747D85',

    /** #1A5159 — user-avatar fill on the person tag (Figma 36465-114337). */
    avatarBg: primitives.neutrals.solid[20],

    accent: primitives.brand[100],
    accentDeep: primitives.brand[120],
    accentDark: primitives.brand[125],
    accentDarker: primitives.brand[150],
    chipBg: primitives.brand[25],
    chipBorder: primitives.brand[75],
    /** #EFF9FA — Figma `colors/brand/10`: active filter-state round-tag fill (no border). */
    tagBg: primitives.brand[10],
    saveViewBg: primitives.brand[50],
    /** primitives.brand[100] at 5% — selected-cell wash */
    selectedCellWash: 'rgba(73, 186, 202, 0.05)',
    /** Selected ledger-row background — pale teal (Figma 36557-201154). */
    selectedRowBg: primitives.brand[10],
    /** #EFF9FA — the report-TYPE band on the mapping statement (Figma 38047-206630).
     *  Same `brand/10` wash as the selected row; named separately because a band is
     *  not a selection and the two must stay free to diverge. */
    groupBandBg: primitives.brand[10],
    /** #F2F5F6 (neutrals.solid[100]) washes at reduced alpha — header row + pivot depth tints */
    surfaceMutedWeak: 'rgba(242, 245, 246, 0.5)',
    depthTint40: 'rgba(242, 245, 246, 0.4)',
    depthTint20: 'rgba(242, 245, 246, 0.2)',
    /** popover / floating-toolbar elevation — brand/128 (#003E47) at 5% */
    popoverShadow: '0px 5px 20px rgba(0, 62, 71, 0.05)',
    /** Lead sheets drag card (Figma 36760-153136): pale-teal fill + light-teal border. */
    dragCardBg: primitives.brand[10],
    dragCardBorder: primitives.brand[85],

    danger: primitives.red[100],
    dangerSoft: primitives.red[75],
    /** #8E1B20 — negative amounts on light surfaces (ledger/pivot cells, numeric
     *  filter preview). Darker than `danger`, which stays the coral alert red. */
    negative: primitives.red[150],
    annotationDot: primitives.orange[100],
    tickmark: primitives.brand[100],
    /** Per-symbol tickmark glyph colours (Figma 36760-157545: ✓ coral/red, SAD
     *  orange, rest brand teal — matches the Editor annotation palette). */
    tickmarkColors: {
      check: primitives.red[100], // ✓ — coral/red
      sad: primitives.orange[100], // SAD
      default: primitives.brand[100], // Rx / ₥ / PY — brand teal
    },

    /**
     * Import-wizard callout tones (CAP-024; Figma `toast-slot`).
     *
     * One pair per tone: Figma's `subtle/default` fill with the `filled/text` ink
     * that is drawn on it. Both halves are stated because the ink is NOT the
     * palette's `main` — a warning callout is #452000 on #FAF0D7, which is a
     * legible pair, where `warning.main` on that fill would not be.
     *
     * The same three tones skin the callout and the check row, which is why they
     * live together rather than beside whichever component used one first.
     */
    importCallout: {
      /**
       * `icon` is a THIRD ink, not a reuse of `text`. The design draws these glyphs
       * FILLED — a solid disc or triangle with the mark knocked out — and a filled
       * shape in the body ink (`blue/filled/text` #204974) would read as a dark blob
       * beside its own copy. Figma gives the fill its own lighter, more saturated
       * step: `blue/filled/default` #2B5888, `warning-yellow/filled/icon` #C68400.
       */
      info: {
        bg: primitives.darkBlue[10],
        text: primitives.darkBlue[125],
        icon: primitives.darkBlue[100],
      },
      success: {
        bg: primitives.green[10],
        text: primitives.green[150],
        icon: primitives.green[100],
      },
      warning: {
        bg: primitives.yellow[10],
        text: primitives.yellow[150],
        icon: primitives.yellow[110],
        /** The deeper amber #874E00 the restore confirmation strokes its circle-alert in (`40660:98544`). */
        iconStrong: primitives.yellow[125],
      },
      /** A gate: the one tone that means the flow has stopped, not merely noticed. */
      danger: { bg: primitives.red[25], text: primitives.red[125], icon: primitives.red[125] },
      /**
       * The wait: Figma `toast-slot role=brand` (`40349:73912`, "Re-checking…") —
       * #055E68 on #EFF9FA, its loader ring in the gradient's teal end #69C4D2.
       */
      brand: { bg: primitives.brand[10], text: primitives.brand[150], icon: primitives.brand[80] },
      /**
       * A check that declined to conclude: Figma `toast-slot` *Not applicable*
       * (`40349:73913`, "Opening balances not checked") — #2C3A42 on the page's own
       * #F8F8FA, since "not checked" is neither a pass nor a problem; the glyph in the
       * not-applicable chip's own muted ink.
       */
      neutral: {
        bg: primitives.neutrals.solid[120],
        text: primitives.grey.primary,
        icon: primitives.neutrals.solid[80],
      },
    },

    /**
     * The step-3 coverage chart (Figma `39012:65755` preview + `38397:249058` card).
     *
     * TWO BAR INKS, because the two charts make different claims. The preview draws
     * the COMPOSED data set in brand teal; a file card draws ONE lane in dark blue.
     * Giving a lane the dataset's colour would let a single file read as the whole.
     *
     * The two overlays are hatched, not washed: they sit BEHIND the bars, and a flat
     * fill at that size is indistinguishable from a tinted plot background. Each is a
     * `bg` with a 2px `stripe` drawn over it on a 6px period.
     */
    importCoverageChart: {
      dataset: primitives.brand[100], // #49BACA
      lane: primitives.darkBlue[100], // #2B5888
      /** A file ALREADY IN THE DATASET (Figma `40380:61580`): context, not this delivery. */
      held: primitives.neutrals.solid[60], // #6A8D93
      /** A file wholly OUTSIDE the picked period, on its own axis (Figma `40383:66379`). */
      outside: primitives.neutrals.solid[80], // #A0B7BA
      overlapBg: primitives.darkBlue[10], // #E0EFFF
      overlapStripe: primitives.darkBlue[25], // #CFE6FF
      gapBg: primitives.yellow[10], // #FAF0D7
      gapStripe: primitives.yellow[15], // #FBDF9E
      /**
       * The out-of-period edge indicator (Figma `40383:65342`, `40383:66379`): the 4px
       * rule where the period cut a file, and the excluded chip's swatch.
       * #F5B02E is SAMPLED off the frames — no palette step lands on it.
       */
      edge: '#F5B02E',
      /**
       * The legend swatches' hairlines (Figma `40383:65342`): each band's own fill with
       * a darker rim, so a 14px square still reads at a glance. Both SAMPLED.
       */
      overlapSwatchBorder: '#8CB8F2',
      gapSwatchBorder: '#EDBF66',
    },

    /**
     * The import wizard's decision block and the option cards inside it (Figma
     * `38486:269455`).
     *
     * A CHOSEN OPTION IS MARKED BY ITS BORDER, not by a fill: the two options carry
     * a paragraph of consequence each, and tinting the whole card would fight that
     * copy for attention while saying nothing the hairline does not.
     */
    /**
     * The Dataset dropdown's `t-test` bordered pills (Figma `40009:77360`): the dataset
     * state badge (§6.2.39) and the version-history row chips.
     *
     * Figma draws two: `positive` (Validated / Active — #F2F9EF on #DCEAD4, #295400
     * ink) and `neutral` (No data / Cancelled — #F8F8FA on #EAF0F1, #414E55 ink). The
     * two it does not draw borrow the Review classes' triples so the page keeps one
     * vocabulary per hue: `info` is *Importing* (a fact about a run, like the running
     * check pill) and `warning` is *Needs revalidation*.
     */
    datasetState: {
      positive: {
        bg: primitives.green[10],
        border: primitives.green[20],
        text: primitives.green[150],
      },
      neutral: {
        bg: primitives.neutrals.solid[120],
        border: primitives.neutrals.solid[100],
        text: primitives.neutrals.textBody[10],
      },
      info: {
        bg: primitives.darkBlue[10],
        border: primitives.darkBlue[25],
        text: primitives.darkBlue[125],
      },
      warning: {
        bg: primitives.yellow[10],
        border: primitives.yellow[15],
        text: primitives.yellow[150],
      },
    },
    /**
     * The Map-accounts statement's "Mapping status" tags (Figma `40344:69039`, the
     * `t-test` ghost pills, 2026-10-08; spec §6.2.58's four-tier trust scale).
     *
     * ONLY *Auto* WEARS ITS TRUST. The machine's placements form a traffic light — green
     * for High, amber for Medium ("worth a look"), red for Low ("flagged guess") — each
     * with its chevron. The decisions a person made wear their STATE: blue for *Manual*
     * (made in this import), grey for *Confirmed* and *Rolled forward* (settled before
     * it), the quietest tags because there is nothing to do.
     *
     * A tier the read could not place (`unknown`, and the row it could not attribute)
     * wears the body ink one step lighter with no fill, so "not evaluated" never reads
     * as a verdict. The seventh cell is not a tag: `unmapped` renders the *assign
     * report line* control, which takes `outlineBorder` on paper.
     */
    mappingProvenance: {
      /** #F8F8FA / #414E55 / #EAF0F1 — the grey ghost pill, Confirmed and Rolled forward. */
      humanBg: primitives.neutrals.solid[120],
      humanText: primitives.neutrals.textBody[10],
      /** #E0EFFF / #204974 / #CFE6FF — the blue ghost pill, Manual. */
      manualBg: primitives.darkBlue[10],
      manualText: primitives.darkBlue[125],
      manualBorder: primitives.darkBlue[25],
      /** #F2F9EF / #295400 / #DCEAD4 — the green ghost pill, High. */
      highBg: primitives.green[10],
      highText: primitives.green[150],
      highBorder: primitives.green[20],
      /** #FAF0D7 / #452000 / #FBDF9E — the yellow ghost pill, Medium. */
      mediumBg: primitives.yellow[10],
      mediumText: primitives.yellow[150],
      mediumBorder: primitives.yellow[15],
      /** #FFF4F3 / #CA2F34 / #FFC8C3 — the red ghost pill, Low. */
      lowBg: primitives.red[25],
      lowText: primitives.red[125],
      lowBorder: primitives.red[60],
      /** The unplaceable tier's ink — no fill, one step lighter than the body. */
      unknownText: primitives.neutrals.textBody[20],
      /** #EAF0F1 — the grey pill's and the *assign report line* pill's hairline, the table's own rule. */
      outlineBorder: primitives.neutrals.solid[100],
    },

    /**
     * Step 3's `t-test` pills — the legend chips under the Σ preview and the tags on a
     * file tile (Figma section `40380:61576`, 2026-10-06): fill, hairline and ink per
     * tone, read off the frames. `neutral` is *Imported*, and *Outside period* on a held
     * file (a label, never a block — user ruling 2026-10-07); `info` the overlap chip;
     * `warning` the gap chip, *Older export* and *N excluded*; `caution` the
     * out-of-period count; `danger` *Removed* and *Outside period* on this import's file.
     *
     * The removed card's own ground and its struck-through name are NOT here: they are
     * `background.default` (#F8F8FA) and `text.disabled` (#B0B5B8).
     */
    importTileTag: {
      neutral: {
        bg: primitives.neutrals.solid[120], // #F8F8FA
        border: primitives.neutrals.solid[100], // #EAF0F1
        text: primitives.grey.secondary, // #414E55
      },
      info: {
        bg: primitives.darkBlue[10], // #E0EFFF
        border: primitives.darkBlue[25], // #CFE6FF
        text: primitives.darkBlue[125], // #204974
      },
      warning: {
        bg: primitives.yellow[10], // #FAF0D7
        border: primitives.yellow[15], // #FBDF9E
        text: primitives.yellow[150], // #452000
      },
      caution: {
        bg: primitives.orange[25], // #FDE4D5
        border: primitives.orange[50], // #F8A76F
        text: primitives.orange[125], // #7D3C00
      },
      danger: {
        bg: primitives.red[25], // #FFF4F3
        border: primitives.red[60], // #FFC8C3
        text: primitives.red[125], // #CA2F34
      },
    },

    /**
     * The read-only import record's banner (Figma `40396:279265`, L8-01): a full-bleed
     * band under the wizard header on every step. A CONFIRMED import is a calm grey
     * (the canvas fill, primary title over secondary body, a light-grey lock); a
     * CANCELLED one is the warning ground with its ink throughout and the deeper amber
     * for the lock.
     */
    importReadOnlyBanner: {
      confirmed: {
        bg: primitives.neutrals.solid[120],
        title: primitives.grey.primary,
        text: primitives.grey.secondary,
        icon: primitives.neutrals.solid[80],
      },
      cancelled: {
        bg: primitives.yellow[10],
        title: primitives.yellow[150],
        text: primitives.yellow[150],
        icon: primitives.yellow[125],
      },
    },

    /**
     * THE SYSTEM-FLAG HIGHLIGHT — a validation view's breaking cells (FEAT-14 §6.2.13,
     * Figma `39226:52513`).
     *
     * ITS OWN TRIPLE, not `importCallout.danger`, and the reason is the decision it
     * encodes rather than the two hexes it shares. §6.2.13 rules that a platform-
     * evaluated finding wears the changed-intersection idiom in VIOLATION styling and
     * never the annotation vocabulary — "a reviewer must never read a system flag as
     * *an auditor checked this*". That makes it a third cell state beside the teal
     * annotation wash and the grey selection wash, and a state with its own reason to
     * exist gets its own name: a later change to the callout's ground must not silently
     * repaint a flagged figure, and vice versa.
     *
     * `border` is the ramp's #FFC8C3 — a hairline, because the flag sits INSIDE a dense
     * numeric grid where a filled cell alone would read as a selection.
     */
    validationFlag: {
      bg: primitives.red[25], // #FFF4F3
      border: primitives.red[60], // #FFC8C3
      text: primitives.red[125], // #CA2F34
    },

    /**
     * The Review step's change-class bands (Figma `39112:120265`, step 6).
     *
     * ONE TRIPLE PER CLASS, because the band IS the count: step 6 no longer draws a
     * row of stat tiles above the groups — the figure lives on the group's own
     * header, and the tint is what tells added from removed before a word is read.
     *
     * `border` is a THIRD value, not `bg` reused. The band is a tinted header over a
     * white body inside one hairline box, and the ramp's fill step is far too light
     * to draw that box — Figma picks the next step up for the edge (#E0EFFF fill /
     * #CFE6FF edge, #FFF4F3 / #FFC8C3, #FAF0D7 / #FBDF9E).
     *
     * NOT `importCallout`: that group's `danger` ink is #CA2F34, the coral of a
     * blocked banner, where a Removed band is drawn in the darker #8E1B20 that the
     * ledger already uses for negative figures. A callout shouts; a class label is
     * read in a row of four.
     */
    importChangeClass: {
      added: {
        bg: primitives.darkBlue[10], // #E0EFFF
        border: primitives.darkBlue[25], // #CFE6FF
        text: primitives.darkBlue[125], // #204974
      },
      changed: {
        bg: primitives.yellow[10], // #FAF0D7
        border: primitives.yellow[15], // #FBDF9E
        text: primitives.yellow[150], // #452000
      },
      removed: {
        bg: primitives.red[25], // #FFF4F3
        border: primitives.red[60], // #FFC8C3
        text: primitives.red[150], // #8E1B20
      },
      /**
       * Unchanged is the one band with no tint of its own — it is the page's own
       * grey, because "nothing happened here" must not compete with the three
       * classes that did. Its inks are the plain text ramp, so they are read from
       * `theme.palette.text` rather than restated here.
       */
      unchanged: {
        bg: primitives.neutrals.solid[120], // #F8F8FA
        border: primitives.neutrals.solid[100], // #EAF0F1
        text: primitives.grey.primary, // #2C3A42
      },
    },

    /**
     * The Review table's CHANGE TAGS — one `square-tag` per changed property on a
     * *Changed* entry (§6.2.37; Figma `39104:119112`, the expanded Review step).
     *
     * A HUE PER PROPERTY, not per tone. The frame colours the four it draws each
     * differently — Amount green, Account teal, Description blue, Side pink — so a
     * row carrying three tags reads as three facts, not as one warning repeated.
     * `offset` borrows Account's pair (it IS an account) and `contact` the neutral
     * one; neither is drawn. `soft` is the two "shown, never counted" properties
     * (Original amount, Payment ID), which §6.2.37 renders muted — same neutral
     * ground, one step lighter ink, so they can never be mistaken for a hard fact.
     * `correction` marks an auditor's correction journal among the entries (#489);
     * Figma `40156:65567` draws it in Amount's green pair.
     */
    importChangeTag: {
      amount: { bg: primitives.green[10], text: primitives.green[150] }, // #F2F9EF / #295400
      account: { bg: primitives.brand[10], text: primitives.brand[150] }, // #EFF9FA / #055E68
      description: { bg: primitives.darkBlue[10], text: primitives.darkBlue[125] }, // #E0EFFF / #204974
      side: { bg: primitives.pink[10], text: primitives.pink[150] }, // #F9E5FF / #742A87
      neutral: { bg: primitives.neutrals.solid[120], text: primitives.neutrals.textBody[10] },
      soft: { bg: primitives.neutrals.solid[120], text: primitives.neutrals.textBody[20] },
      correction: { bg: primitives.green[10], text: primitives.green[150] }, // #F2F9EF / #295400
    },

    /**
     * Ink for the figure on an import-wizard stat tile (Figma step 6).
     *
     * A tile's number is coloured by what it MEANS — additions read blue, changes
     * amber, removals red, and the two informational counts stay neutral — so the
     * five tiles are scannable before a single label is read. Separate from
     * `importCallout` because these are inks on white, not fill/ink pairs.
     */
    importStat: {
      info: primitives.darkBlue[125],
      warning: primitives.yellow[150],
      negative: primitives.red[125],
      neutral: primitives.neutrals.textBody[0],
      muted: primitives.neutrals.textBody[10],
    },

    signoff: {
      preparerWarnBg: primitives.yellow[10],
      preparerWarnText: primitives.orange[125],
      preparerWarnAction: primitives.orange[150],
      reviewerSuccessBg: primitives.green[10],
      reviewerSuccessText: primitives.green[150],
    },

    // "Unmapped" report-line group (Lead sheets pivot): the amber row highlight.
    //
    // The vivid-orange ACTION step that used to live here (Figma 36760-153112 / -155579,
    // `orange/100` #F17F16) is gone with the affordance it painted: the picker's "Set to
    // Unmapped" footer was removed on the product decision that accounts are remapped,
    // never unmapped (FEAT-08 §6.2.14). Figma still draws both; neither comes back without
    // a stakeholder decision.
    unmapped: {
      /**
       * #FEF9E7 — SAMPLED from the Figma row itself (36760-51639), not derived from the
       * ramp: no palette step matches it, it is a wash the design mixes for this one row.
       *
       * OPAQUE on purpose. It was `rgba(254, 243, 209, 0.5)`, which (a) named a
       * pre-Figma-migration hex that no longer exists in the palette and (b) could not be
       * used by the pivot at all — `PivotTable.styles.ts` requires opaque row fills
       * because the frozen label/gutter cells take the row colour via `inherit`, so a
       * translucent value composites differently there than over the scrolling cells.
       */
      bg: '#FEF9E7',
      /**
       * #7D3C00 — Figma `colors/orange/150`, the Unmapped group-row label (36760-51639).
       *
       * `primitives.orange[150]` is NOT that step: the Figma palette migration re-pointed
       * it at `figma.orange[175]` (#471E00), a near-black brown. The Figma ramp's `/150`
       * lands on `primitives.orange[125]`, so the name-to-name mapping is the wrong one
       * here and the number has to be chased through the ramp, not read off the suffix.
       */
      text: primitives.orange[125],
    },

    // Status dot/icon colours sampled from Figma (FEAT-04 §4.2 audit 02-#9/#10/#11/#14).
    status: {
      draft: primitives.neutrals.solid[80], // #CCD8DA light grey
      in_progress: primitives.brand[100], // #49BACA teal
      blocked: primitives.orange[100], // #F17F16 orange (Figma; spec text "red" updated to match)
      prepared: primitives.yellow[100], // #FCC419 amber
      reviewed: primitives.green[100], // #569720 mid green
    },
  },
};
